<template>
  <div
    class="w-full overflow-hidden"
    :style="{ 'aspect-ratio': cssAspectRatio }"
  >
    <!-- Parallax on/off branch -->
    <AnimationsGsapOnScroll
      v-if="!disableParallax"
      class="w-full"
    >
      <picture class="w-full h-full">
        <!-- one <source> per breakpoint -->
        <source
          v-for="entry in imageData"
          :key="entry.breakpoint"
          :media="
            entry.breakpoint === smallestBreakpoint
              ? `(max-width: ${entry.size}px)`
              : `(min-width: ${entry.size}px)`
          "
          :srcset="`${getImageUrl(entry.picture.w, entry.picture.h)} ${entry.size}w`"
        />
        <!-- fallback <img> -->
        <img
          ref="image"
          class="object-cover w-full h-full"
          data-scroll
          loading="lazy"
          :alt="alt || 'Default alt tag'"
          :src="fallbackSrc"
          :width="renderWidth"
          :height="renderHeight"
          @load="onImageLoad"
        />
      </picture>
    </AnimationsGsapOnScroll>

    <!-- when parallax is disabled -->
    <picture
      v-else
      class="w-full h-full"
    >
      <source
        v-for="entry in imageData"
        :key="entry.breakpoint"
        :media="
          entry.breakpoint === smallestBreakpoint
            ? `(max-width: ${entry.size}px)`
            : `(min-width: ${entry.size}px)`
        "
        :srcset="`${getImageUrl(entry.picture.w, entry.picture.h)} ${entry.size}w`"
      />
      <img
        ref="image"
        class="object-cover w-full h-full"
        data-scroll
        loading="lazy"
        :alt="alt || 'Default alt tag'"
        :src="fallbackSrc"
        :width="renderWidth"
        :height="renderHeight"
        @load="onImageLoad"
      />
    </picture>
  </div>
</template>

<script setup>
import imageUrlBuilder from '@sanity/image-url';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from './../tailwind.config';
import getImageDataFromRef from './../queries/getImageDataFromRef';

const props = defineProps({
  aspectRatios: {
    type: Array,
    default: () => [
      { sm: 'default' },
      { md: 'default' },
      { lg: 'default' },
      { xl: 'default' },
      { '2xl': 'default' },
      { '3xl': 'default' }
    ]
  },
  refImage: String,
  isLazyloaded: Boolean,
  disableParallax: Boolean,
  alt: String
});

const image = ref(null);
const picture = reactive({ asset: null, width: 0, height: 0 });
const imageData = ref([]);
const renderWidth = ref(0);
const renderHeight = ref(0);
const fallbackSrc = ref('');

const sanity = useSanity();
// pull Tailwind breakpoints
const screens = resolveConfig(tailwindConfig).theme.screens;
// track viewport width
const windowWidth = ref(0);
function updateWidth() {
  windowWidth.value = window.innerWidth;
}

// build and sort breakpoints descending
const breakpoints = computed(() =>
  Object.entries(screens)
    .map(([bp, px]) => ({
      bp,
      minWidth: parseInt(px.replace('px', ''), 10),
      ratio: props.aspectRatios.find((r) => Object.keys(r)[0] === bp)?.[bp] || 'default'
    }))
    .sort((a, b) => b.minWidth - a.minWidth)
);

// helper to build Sanity URLs
function createBuilder() {
  return imageUrlBuilder(sanity.config);
}

// calculate height for a given width + ratio
function getAspectRatioHeight(width, ratio) {
  if (ratio === 'square') return width;
  if (ratio === 'panorama') return Math.round((width / 147) * 36.75);
  if (ratio.includes(':')) {
    const [wR, hR] = ratio.split(':').map(parseFloat);
    return Math.round((width / wR) * hR);
  }
  // 'default' → preserve original aspect
  return Math.round((width * picture.height) / picture.width);
}

// build the array of { breakpoint, size, picture:{w,h}, ratio }
function generateImageData() {
  return breakpoints.value.map(({ bp, minWidth, ratio }) => {
    let w, h;
    if (picture.width > minWidth) {
      w = minWidth;
      h = getAspectRatioHeight(minWidth, ratio);
    } else {
      w = picture.width;
      h = getAspectRatioHeight(picture.width, ratio);
    }
    return { breakpoint: bp, size: minWidth, picture: { w, h }, ratio };
  });
}

// get the URL for a given w/h
function getImageUrl(w, h) {
  return createBuilder()
    .image(picture.asset)
    .width(w)
    .height(h)
    .auto('format')
    .dpr(1)
    .fit('crop')
    .url();
}

// on <img> load, capture actual rendered size
function onImageLoad() {
  if (!image.value) return;
  const w = image.value.offsetWidth;
  const h = image.value.offsetHeight;
  // only rebuild if we actually measured a size
  if (w > 0 && h > 0) {
    renderWidth.value = w;
    renderHeight.value = h;
    fallbackSrc.value = getImageUrl(w, h);
  }
}

// pick the smallest breakpoint key (for max-width media)
const smallestBreakpoint = computed(() => imageData.value[imageData.value.length - 1]?.breakpoint);

// pick which ratio applies now (by checking windowWidth)
const currentAspectRatio = computed(() => {
  for (const { minWidth, ratio } of breakpoints.value) {
    if (windowWidth.value >= minWidth) {
      return ratio;
    }
  }
  // fallback to the last
  return breakpoints.value[breakpoints.value.length - 1]?.ratio || 'default';
});

// convert “3:1”→“3/1”, “square”→“1/1”, or natural
const cssAspectRatio = computed(() => {
  const r = currentAspectRatio.value;
  if (r === 'default') {
    return picture.width && picture.height ? `${picture.width}/${picture.height}` : 'auto';
  }
  if (r === 'square') return '1/1';
  if (r === 'panorama') return '147/36.75';
  if (r.includes(':')) return r.replace(':', '/');
  return 'auto';
});

// ——— Fetch & setup ———
onMounted(async () => {
  // watch for resize
  updateWidth();
  window.addEventListener('resize', updateWidth);

  // fetch your image document
  const doc = await sanity.fetch(getImageDataFromRef(props.refImage));
  const { asset } = doc;
  picture.asset = asset;
  picture.width = asset.metadata.dimensions.width;
  picture.height = asset.metadata.dimensions.height;

  // build each source’s dimensions
  imageData.value = generateImageData();

  // set an initial fallback (smallest)
  const first = imageData.value[0];
  fallbackSrc.value = getImageUrl(first.picture.w, first.picture.h);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateWidth);
});
</script>

<style lang="postcss">
img {
  @apply w-full;
}
</style>
